import { type FC, memo, type MouseEventHandler, useEffect, useState } from "react";
import Card, { CardSize } from "../../../../common/components/Card";
import { getRedirectToRegisterPage, redirectToUrl } from "../../../../utils/url/urlUtils";
import Parameter from "../../../../common/components/Parameter";
import { useDispatch, useSelector } from "react-redux";
import {
  getCurrentStep,
  getSteps,
  selectIsConfiguratorAddedToCartAlready,
  selectIsConfiguratorAddToCartEnabled,
} from "../../../Linear/redux/selectors";
import { useBooleanState } from "../../../../common/hooks/useBooleanState";
import LinearATCPopup from "./LinearATCPopup";
import { LinearProductReviewButton, LinearProductReviewCard } from "./LinearProductReview";
import { IconType } from "../../../../common/components/icon/IconType";
import { Permission, userHasAnyPermission, userHasPermission } from "../../../../utils/permissions";

import LinearRequestQuotePopup from "../../../Linear/components/LinearRequestQuotePopup";
import { userIsCustomer, userIsRegistered } from "../../../../utils/roles";
import { userCanPurchaseLinear } from "../../../Linear/components/Substeps/useLinearPrice";
import { goToCadSummary } from "../../../Linear/redux/actions";
import { useTrackLCSimpleButtonClick } from "../../../Linear/hooks/useTrackLCClicks";
import { SCROLL_TO_LC_SUMMARY_TABS } from "../ConfigurationSummarySubstep";
import { CART_PAGE } from "../../../../utils/url/staticUrls";
import { Button } from "~/common/components/Button/Button";
import { useTranslation } from "react-i18next";
import { useLinearAvailability } from "~/Configurators/Linear/components/Substeps/useLinearAvailability";
import CircleSpinner from "~/common/components/CircleSpinner/CircleSpinner";
import { Stacker } from "~/common/components/Stacker/Stacker";
import { DefaultButtonIcon, Icon } from "~/common/components/icon/Icon";
import { IconSize } from "~/common/components/icon/IconSize";

const LinearSummary: FC = () => {
  const stepsList = useSelector(getSteps);

  const disableAddToCart = !useSelector(selectIsConfiguratorAddToCartEnabled);
  const showAddToCart = userHasAnyPermission(Permission.ADD_TO_CART, Permission.ADD_TO_QUOTE);

  const { val: isModalOpen, setTrue: openModal, setFalse: closeModal } = useBooleanState(false);
  const [isRequestQuoteOpen, setIsRequestQuoteOpen] = useState<boolean>(false);
  const openCloseRequestQuote = () => setIsRequestQuoteOpen((prev) => !prev);

  const { t } = useTranslation();

  const [reviewOpen, setReviewOpen] = useState(false);

  const { leadTime, isLoadingAvailability, errorLoadingAvailability } = useLinearAvailability();

  const isCustomer = userIsCustomer();
  const canSeeAvailability = userHasPermission(Permission.AVAILABILITY);
  const isRegistered = userIsRegistered();
  const isLatestKmatUser = userCanPurchaseLinear();

  let leadTimeElement = null;
  if (isCustomer && isLatestKmatUser && canSeeAvailability) {
    if (isLoadingAvailability) {
      leadTimeElement = <CircleSpinner />;
    } else {
      let availabilityText;
      if (errorLoadingAvailability || !leadTime) {
        availabilityText = "-";
      } else {
        availabilityText = t("page.linear.summary.weeks", { count: leadTime });
      }
      leadTimeElement = (
        <Parameter className={"m-0"} label={t("page.linear.summary.availability")} value={availabilityText} />
      );
    }
  }

  const dispatch = useDispatch();

  const trackCADClick = useTrackLCSimpleButtonClick("click_on_CAD_button");
  const currentStepIndex = useSelector(getCurrentStep);
  const isOnSummaryStep = stepsList.length - 1 === currentStepIndex;

  const redirectToCad = () => {
    if (!isCustomer && !isRegistered) {
      redirectToUrl(
        getRedirectToRegisterPage({ targetPage: document.location.href + "?showCAD=true", openLogin: true }),
      );
    } else {
      trackCADClick();
      dispatch(goToCadSummary());
      if (isOnSummaryStep) {
        SCROLL_TO_LC_SUMMARY_TABS();
      }
    }
  };

  useEffect(() => {
    const logged = window.location.search;
    if (logged === "?showCAD=true") {
      redirectToCad();
    }
  }, []);

  const disableCadButton =
    stepsList && stepsList.length > 0 && stepsList[stepsList.length - 1].active === true ? false : true;

  const isAddedToCartAlready = useSelector(selectIsConfiguratorAddedToCartAlready);

  return (
    <>
      <Card size={CardSize.SMALL} className={"scha__linear-configurator__header__summary"}>
        <Stacker direction={"row"} gap={32}>
          {leadTimeElement}
          <Button
            className={"scha__lc__cad__btn"}
            size={"medium"}
            data-test-id={"linear_cadButton"}
            onClick={redirectToCad}
            disabled={disableCadButton}
          >
            <Icon size={IconSize.FONT_24} type={IconType.Cad} />
          </Button>
          {isCustomer && showAddToCart && !isAddedToCartAlready && (
            <AddToCartButton
              disabled={disableAddToCart || webservicesDisabled}
              onClick={isLatestKmatUser ? openModal : openCloseRequestQuote}
            />
          )}
          {isCustomer && showAddToCart && isAddedToCartAlready && <GoToCartButton />}
          <LinearProductReviewButton isOpen={reviewOpen} setIsOpen={setReviewOpen} />
        </Stacker>
        {reviewOpen && <LinearProductReviewCard isOpen={reviewOpen} setIsOpen={setReviewOpen} />}
      </Card>
      {isModalOpen && <LinearATCPopup onClose={closeModal} />}
      {isRequestQuoteOpen && <LinearRequestQuotePopup onClose={openCloseRequestQuote} />}
    </>
  );
};

interface ButtonProps {
  className?: string;
  onClick?: MouseEventHandler<HTMLElement>;
  href?: string;
  disabled?: boolean;
}

const webservicesDisabled = window.SCHA.config.disableAllWS;

const AddToCartButton = (props: ButtonProps) => {
  const { t } = useTranslation();
  const canPurchase = userHasPermission(Permission.LINEAR_PURCHASE);
  const label = t(`linearConfigSummary.card.${canPurchase ? "addToCart" : "addToQuote"}`);

  return (
    <Button
      startIcon={<DefaultButtonIcon type={IconType.Cart} />}
      data-test-id={"linear_addToCartButton"}
      disabled={webservicesDisabled || (props.disabled == null ? false : props.disabled)}
      {...props}
      onClick={props.onClick}
    >
      {label}
    </Button>
  );
};

const GoToCartButton = () => {
  const { t } = useTranslation();

  return (
    <Button
      startIcon={<DefaultButtonIcon type={IconType.Cart} />}
      data-test-id={"linear_addToCartButton"}
      href={CART_PAGE}
      disabled={webservicesDisabled}
    >
      {t(`page.linear.addToCart.button.goToCart`)}
    </Button>
  );
};

export default memo(LinearSummary);
